import { useCallback } from "react";
import { useSearchParams } from "react-router";
import type { List } from "@guestbook/shared";
import { useLists } from "@/shared/lib/lists";

/**
 * Single source of truth for the active list scope. The scope is encoded in
 * the URL as `?list=<listId>` and propagates across all contact subroutes.
 *
 * Components and mutations consult this hook instead of reading the URL
 * directly so the "am I scoped?" branch lives in exactly one place per
 * action site. New scope-aware behavior is added by reading this hook,
 * not by sprinkling `searchParams.get("list")` calls.
 */
export interface ActiveScope {
  /** The list id from `?list=` in the URL, or null when unscoped. */
  listId: string | null;
  /**
   * The full List record when the URL listId matches a list the user owns.
   * `null` when unscoped, when the lists query is still loading, or when
   * the URL references an unknown list (treated as "All Contacts").
   */
  list: List | null;
  /** True when listId is present AND resolves to a known list. */
  isScoped: boolean;
}

export function useActiveScope(): ActiveScope {
  const [searchParams] = useSearchParams();
  const listId = searchParams.get("list");
  const lists = useLists();
  const list = listId
    ? lists.data?.items.find((l) => l.id === listId) ?? null
    : null;
  return {
    listId,
    list,
    isScoped: Boolean(list),
  };
}

/**
 * Appends `?list=<id>` to a path when a scope is active. Pure — safe to
 * call from anywhere; pass `null` to get back the path unchanged. Existing
 * query strings on `path` are preserved.
 */
export function withListParam(
  path: string,
  listId: string | null | undefined,
): string {
  if (!listId) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}list=${encodeURIComponent(listId)}`;
}

/**
 * Returns a stable function that prefixes any path with the current list
 * scope, so Links and navigate() calls can share scope without each one
 * reading the URL itself.
 *
 *   const href = useScopedHref();
 *   <Link to={href("/app/contacts/new")} />
 */
export function useScopedHref(): (path: string) => string {
  const [searchParams] = useSearchParams();
  const listId = searchParams.get("list");
  return useCallback((path: string) => withListParam(path, listId), [listId]);
}
